import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * FileUpload component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconFileUpload extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.85449 16.7129C2.26871 16.7129 2.60449 17.0487 2.60449 17.4629V19.0237C2.60449 19.6526 2.85435 20.2558 3.2991 20.7006C3.74385 21.1453 4.34706 21.3952 4.97603 21.3952H19.023C19.6519 21.3952 20.2551 21.1453 20.6999 20.7006C21.1446 20.2558 21.3945 19.6526 21.3945 19.0237V17.4629C21.3945 17.0487 21.7303 16.7129 22.1445 16.7129C22.5587 16.7129 22.8945 17.0487 22.8945 17.4629V19.0237C22.8945 20.0505 22.4866 21.0352 21.7605 21.7613C21.0345 22.4873 20.0498 22.8952 19.023 22.8952H4.97603C3.94923 22.8952 2.96449 22.4873 2.23844 21.7613C1.51239 21.0352 1.10449 20.0505 1.10449 19.0237V17.4629C1.10449 17.0487 1.44028 16.7129 1.85449 16.7129Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.9998 1.10516C12.2309 1.10516 12.4376 1.20974 12.5752 1.37416L17.2515 6.82976C17.521 7.14426 17.4846 7.61773 17.1701 7.8873C16.8556 8.15686 16.3821 8.12044 16.1126 7.80595L12.7498 3.88267V15.9021C12.7498 16.3163 12.414 16.6521 11.9998 16.6521C11.5855 16.6521 11.2498 16.3163 11.2498 15.9021V3.88255L7.88684 7.80595C7.61727 8.12044 7.1438 8.15686 6.8293 7.8873C6.51481 7.61773 6.47839 7.14426 6.74795 6.82976L11.4303 1.36707C11.4641 1.32757 11.5017 1.29206 11.5422 1.26087C11.6688 1.16324 11.8275 1.10516 11.9998 1.10516Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-file-upload') === undefined) {
  customElements.define('cat-icon-file-upload', BlocksIconFileUpload);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-file-upload': BlocksIconFileUpload;
  }
}
